import React from 'react';
import { Text, StyleSheet, Platform, View } from 'react-native';
import DLOModal from '../../../components/DLOModal';
import { ModalState, OverHoursValidationDetail, UnassignedRoleValidationDetail, ForecastVarianceDetail } from '../types';

interface DLOCreateScheduleModalsProps {
  modalState: ModalState;
  setModalState: {
    setValidationErrorModal: (show: boolean, message: string, title?: string) => void;
    setSuccessModal: (show: boolean, message: string) => void;
    setSubmitConfirmationModal: (show: boolean, message: string) => void;
    setSubmitWarningModal: (show: boolean, message: string, details?: OverHoursValidationDetail[] | ForecastVarianceDetail[]) => void;
    setSubmitErrorModal: (show: boolean, message: string, details?: UnassignedRoleValidationDetail[]) => void;
    setUnsavedChangesModal: (show: boolean, message: string) => void;
  };
  onResetSaveState: () => void;
  onPerformSubmission: () => void;
  onProceedWithUnsavedChanges?: () => void;
}

const DLOCreateScheduleModals: React.FC<DLOCreateScheduleModalsProps> = ({
  modalState,
  setModalState,
  onResetSaveState,
  onPerformSubmission,
  onProceedWithUnsavedChanges,
}) => {
  return (
    <>
      {/* Validation Error Modal */}
      <DLOModal
        visible={modalState.showValidationErrorModal}
        onClose={() => setModalState.setValidationErrorModal(false, '')}
        title={modalState.validationErrorTitle || 'Missing Employee Complete Details'}
        primaryButtonText="Review"
        onPrimaryPress={() => setModalState.setValidationErrorModal(false, '')}
        hideButtons={false}
        showCloseIcon={true}
        titleAlign="center"
      >
        <Text style={styles.errorModalText}>
          {modalState.validationErrorMessage}
        </Text>
      </DLOModal>

      {/* Success Modal */}
      <DLOModal
        visible={modalState.showSuccessModal}
        onClose={() => {
          setModalState.setSuccessModal(false, '');
          onResetSaveState();
        }}
        title="Success"
        primaryButtonText="OK"
        onPrimaryPress={() => {
          setModalState.setSuccessModal(false, '');
          onResetSaveState();
        }}
        hideButtons={false}
        showCloseIcon={true}
        titleAlign="center"
      >
        <Text style={styles.successModalText}>
          {modalState.successMessage}
        </Text>
      </DLOModal>

      {/* Submit Confirmation Modal - Case 1: Both validations pass */}
      <DLOModal
        visible={modalState.showSubmitConfirmationModal}
        onClose={() => setModalState.setSubmitConfirmationModal(false, '')}
        title="Confirm Submission"
        primaryButtonText="Yes, Submit"
        secondaryButtonText="Cancel"
        onPrimaryPress={() => {
          setModalState.setSubmitConfirmationModal(false, '');
          onPerformSubmission();
        }}
        onSecondaryPress={() => setModalState.setSubmitConfirmationModal(false, '')}
        hideButtons={false}
        showCloseIcon={true}
        titleAlign="center"
        width={548}
      >
        <Text style={styles.confirmationModalText}>
          {modalState.submitConfirmationMessage}
        </Text>
      </DLOModal>

      {/* Submit Warning Modal - Case 2: Hours validation fails, roles pass */}
      <DLOModal
        visible={modalState.showSubmitWarningModal}
        onClose={() => setModalState.setSubmitWarningModal(false, '', undefined)}
        title="Over forecast limit"
        primaryButtonText="Submit anyway"
        secondaryButtonText="Cancel"
        onPrimaryPress={() => {
          setModalState.setSubmitWarningModal(false, '', undefined);
          onPerformSubmission();
        }}
        onSecondaryPress={() => setModalState.setSubmitWarningModal(false, '', undefined)}
        hideButtons={false}
        showCloseIcon={true}
        titleAlign="center"
        width={548}
      >
        <View>
          {/* Main Warning Box */}
          <View style={styles.warningBox}>
            <View style={styles.warningIconContainer}>
              <Text style={styles.warningIcon}>⚠</Text>
            </View>
            <Text style={styles.warningBoxText}>
              {modalState.submitWarningMessage}
            </Text>
          </View>
          
          {/* Details Box */}
          <View style={styles.detailsBox}>
            {modalState.submitWarningDetails && modalState.submitWarningDetails.map((detail, index) => {
              // Check if this is a ForecastVarianceDetail or OverHoursValidationDetail
              const isForecastVariance = 'roleName' in detail && 'type' in detail && 'variance' in detail;
              
              return (
                <View key={index} style={styles.detailItem}>
                  <View style={styles.detailIconContainer}>
                    <Text style={styles.detailIcon}>⚠</Text>
                  </View>
                  <View style={styles.detailContent}>
                    {isForecastVariance ? (
                      // Forecast variance details
                      <>
                        <Text style={styles.detailEmployeeName}>
                          {(detail as ForecastVarianceDetail).roleName} is <Text style={{ fontWeight: 'bold' }}>{(detail as ForecastVarianceDetail).type} forecasted hours </Text> by {(detail as ForecastVarianceDetail).variance.toFixed(1)} hours.
                        </Text>
                        <Text style={styles.detailDays}>
                          Forecasted: {(detail as ForecastVarianceDetail).forecastedHours}h, Scheduled: {(detail as ForecastVarianceDetail).scheduledHours}h
                        </Text>
                        {(detail as ForecastVarianceDetail).exceededDays.length > 0 && (
                          <Text style={styles.detailDays}>
                            Days affected: {(detail as ForecastVarianceDetail).exceededDays.join(', ')}
                          </Text>
                        )}
                      </>
                    ) : (
                      // Over hours validation details (existing logic)
                      <>
                        <Text style={styles.detailEmployeeName}>
                          {(detail as OverHoursValidationDetail).employeeName} exceeds 40 hours per week.
                        </Text>
                        <Text style={styles.detailDays}>
                          Total: {(detail as OverHoursValidationDetail).totalHours}h ({(detail as OverHoursValidationDetail).exceededDays.join(', ')})
                        </Text>
                      </>
                    )}
                  </View>
                </View>
              );
            })}
          </View>
        </View>
      </DLOModal>

      {/* Submit Error Modal - Case 3: Role validation fails */}
      <DLOModal
        visible={modalState.showSubmitErrorModal}
        onClose={() => setModalState.setSubmitErrorModal(false, '', undefined)}
        title="Additional Review Required"
        primaryButtonText="Review"
        onPrimaryPress={() => setModalState.setSubmitErrorModal(false, '', undefined)}
        hideButtons={false}
        showCloseIcon={true}
        titleAlign="center"
        width={548}
      >
        <View>
          {/* Main Error Box */}
          <View style={styles.errorBox}>
            <View style={styles.errorIconContainer}>
              <Text style={styles.errorIcon}>⚠</Text>
            </View>
            <Text style={styles.errorBoxText}>
              You can't submit: some roles have <Text style={{ fontWeight: 'bold' }}>forecasted hours</Text> but no schedules assigned
            </Text>
          </View>
          
          {/* Details Box */}
          <View style={styles.detailsBox}>
            {modalState.submitErrorDetails && modalState.submitErrorDetails.length > 0 ? (
              modalState.submitErrorDetails.map((detail, index) => (
                <View key={index} style={styles.detailItem}>
                  <View style={styles.detailIconContainer}>
                    <Text style={styles.detailErrorIcon}>!</Text>
                  </View>
                  <View style={styles.detailContent}>
                    <Text style={styles.detailErrorText}>
                      {detail.type === 'missing_role' 
                        ? `${detail.employeeName} does not have a role assigned`
                        : (
                          <>
                            {detail.roleName} has <Text style={{ fontWeight: 'bold' }}>forecasted hours</Text> but no assigned employees
                          </>
                        )
                      }
                    </Text>
                  </View>
                </View>
              ))
            ) : (
              <Text style={styles.detailErrorText}>No detailed validation information available</Text>
            )}
          </View>
        </View>
      </DLOModal>

      {/* Unsaved Changes Warning Modal */}
      <DLOModal
        visible={modalState.showUnsavedChangesModal}
        onClose={() => setModalState.setUnsavedChangesModal(false, '')}
        title="Unsaved Changes"
        primaryButtonText="Yes, Proceed"
        secondaryButtonText="Cancel"
        onPrimaryPress={() => {
          setModalState.setUnsavedChangesModal(false, '');
          onProceedWithUnsavedChanges?.();
        }}
        onSecondaryPress={() => setModalState.setUnsavedChangesModal(false, '')}
        hideButtons={false}
        showCloseIcon={true}
        titleAlign="center"
      >
        <Text style={styles.warningModalText}>
          {modalState.unsavedChangesMessage}
        </Text>
      </DLOModal>
    </>
  );
};

const styles = StyleSheet.create({
  errorModalText: {
    fontSize: 16,
    color: '#dc3545',
    textAlign: 'center',
    lineHeight: 24,
    paddingHorizontal: 16,
    paddingVertical: 8,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  successModalText: {
    fontSize: 16,
    color: '#28a745',
    textAlign: 'center',
    lineHeight: 24,
    paddingHorizontal: 16,
    paddingVertical: 8,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  confirmationModalText: {
    fontSize: 16,
    color: '#333333',
    textAlign: 'center',
    lineHeight: 24,
    paddingHorizontal: 16,
    paddingVertical: 8,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  warningModalText: {
    fontSize: 16,
    color: '#f57c00',
    textAlign: 'center',
    lineHeight: 24,
    paddingHorizontal: 16,
    paddingVertical: 8,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  // Warning Modal Styles
  warningBox: {
    backgroundColor: '#FFF3CD',
    borderRadius: 8,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#F5C842',
  },
  warningIconContainer: {
    marginRight: 12,
  },
  warningIcon: {
    fontSize: 20,
    color: '#856404',
  },
  warningBoxText: {
    fontSize: 16,
    color: '#856404',
    flex: 1,
    lineHeight: 20,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  // Error Modal Styles
  errorBox: {
    backgroundColor: '#F8D7DA',
    borderRadius: 8,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#F1AEB5',
  },
  errorIconContainer: {
    marginRight: 12,
  },
  errorIcon: {
    fontSize: 20,
    color: '#721C24',
  },
  errorBoxText: {
    fontSize: 16,
    color: '#721C24',
    flex: 1,
    lineHeight: 20,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  // Details Box (shared between warning and error)
  detailsBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E9ECEF',
    // maxHeight: 200,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  detailIconContainer: {
    marginRight: 8,
    marginTop: 2,
  },
  detailIcon: {
    fontSize: 16,
    color: '#856404',
  },
  detailErrorIcon: {
    fontSize: 16,
    color: '#FFFFFF',
    fontWeight: 'bold',
    backgroundColor: '#DC3545',
    borderRadius: 10,
    width: 20,
    height: 20,
    textAlign: 'center',
    lineHeight: 20,
  },
  detailContent: {
    flex: 1,
  },
  detailEmployeeName: {
    fontSize: 14,
    color: '#856404',
    fontWeight: '500',
    lineHeight: 18,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  detailErrorText: {
    fontSize: 14,
    color: '#DC3545',
    fontWeight: '500',
    lineHeight: 18,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  detailDays: {
    fontSize: 14,
    color: '#856404',
    lineHeight: 18,
    marginTop: 2,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
});

export default DLOCreateScheduleModals;